import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';

interface Props {
  active: {
    id: string;
    eventKind?: string;
    startedAtLocal: string;
    endedAtLocal: string | null;
    curtailmentPct: number | null;
    curtailmentPmaxKw: number;
    source: string;
    reason: string | null;
  } | null | undefined;
  assetName?: string;
}

export default function CurtailmentBanner({ active, assetName }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  if (!active) return null;

  // Event-kind classification drives BOTH copy and colors.
  // OSR = amber (non-compensated, hard cap on submitted forecast).
  // SKO = violet (compensated, does NOT alter forecast — informational only).
  const isOsr = active.eventKind === 'osr_command';
  const isSko = active.eventKind === 'sko_command';

  const startedLocal = new Date(active.startedAtLocal);
  const startedFmt = startedLocal.toLocaleString('uk-UA', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const pctTxt = active.curtailmentPct != null ? `${active.curtailmentPct.toFixed(0)}%` : '—';

  const titleKey = isOsr
    ? 'curtailment_banner_title_osr'
    : isSko
      ? 'curtailment_banner_title_sko'
      : 'curtailment_banner_title';

  const sourceTxt = active.source === 'auto_detected'
    ? t('curtailment_banner_source_auto')
    : active.source === 'dispatcher_phone'
      ? t('curtailment_banner_source_dispatcher_phone')
      : active.source === 'dispatcher_email'
        ? t('curtailment_banner_source_dispatcher_email')
        : active.source === 'sko_command'
          ? t('curtailment_banner_source_sko')
          : active.source === 'analyst_reconstruction'
            ? t('curtailment_banner_source_analyst')
            : active.source;

  const bg = isOsr
    ? (isDark ? 'rgba(245,158,11,0.10)' : '#FEF3C7')
    : isSko
      ? (isDark ? 'rgba(139,92,246,0.10)' : '#EDE9FE')
      : (isDark ? 'rgba(155,155,164,0.10)' : '#F3F4F6');
  const border = isOsr
    ? (isDark ? 'rgba(245,158,11,0.30)' : '#FDE68A')
    : isSko
      ? (isDark ? 'rgba(139,92,246,0.30)' : '#DDD6FE')
      : (isDark ? 'rgba(155,155,164,0.20)' : '#E5E5E8');
  const text = isOsr
    ? (isDark ? '#FCD34D' : '#92400E')
    : isSko
      ? (isDark ? '#C4B5FD' : '#5B21B6')
      : (isDark ? '#D0D0D5' : '#1A1A1A');

  // Route button to the matching page: /osr for OSR commands, /sko for SKO commands.
  const detailsRoute = isOsr ? '/osr' : '/sko';
  const detailsKey = isOsr ? 'curtailment_banner_view_osr' : 'curtailment_banner_view_sko';

  return (
    <div className="rounded-lg px-4 py-3 mb-4 flex items-start gap-3"
      style={{ background: bg, border: `1px solid ${border}`, color: text }}>
      <i className="ri-error-warning-line text-xl mt-0.5"></i>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">
          {t(titleKey)}
          {assetName && <span className="ml-1 font-normal opacity-80">— {assetName}</span>}
        </div>
        <div className="text-xs mt-1 leading-snug">
          {t('curtailment_banner_cap_label')}: <strong>{(active.curtailmentPmaxKw ?? 0).toFixed(0)} {t('ak_fc_kw')}</strong>
          {' '}({pctTxt} {t('curtailment_banner_pct_suffix')})
          {' · '}{t('curtailment_banner_since')} {startedFmt}
          {' · '}{t('curtailment_banner_source')}: {sourceTxt}
        </div>
        {active.reason && (
          <div className="text-[11px] mt-1 opacity-80">{active.reason}</div>
        )}
      </div>
      <button type="button" onClick={() => navigate(detailsRoute)}
        className="text-xs px-3 py-1 rounded cursor-pointer whitespace-nowrap font-medium"
        style={{ background: 'rgba(0,0,0,0.05)', color: text, border: `1px solid ${border}` }}>
        {t(detailsKey)} →
      </button>
    </div>
  );
}
